import React, { useState } from 'react';
import { API_BASE } from '../../config/api';
import { isValidEmail } from '../../utils/authValidation';
import { Eyebrow } from '../Layout/PageLayout';
import {
  AuthPage,
  AuthLayout,
  AuthCard,
  CardBody,
  CardFoot,
  CardIcon,
  PanelTitle,
  PanelText,
  Form,
  Field,
  Input,
  MessageBox,
  PrimaryButton,
  GhostButton,
  InlineLink,
} from './AuthSharedStyles';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/forgot-password?client_url=${encodeURIComponent(window.location.origin)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to start password reset');
      setMessage(data.message || 'If an account exists for this email, a reset link has been sent.');
    } catch (err) {
      setError(err.message || 'Failed to start password reset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPage>
      <AuthLayout $centered>
        <AuthCard>
          <CardBody>
            {message ? (
              <div role="status">
                <CardIcon $round $success>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                </CardIcon>
                <PanelTitle>Check your inbox</PanelTitle>
                <PanelText>{message} The email can take a minute to arrive. If it does not, check the spam folder.</PanelText>
                <GhostButton type="button" onClick={() => setMessage('')}>
                  Use a different email
                </GhostButton>
              </div>
            ) : (
              <>
                <CardIcon>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="8" cy="15" r="4" />
                    <path d="M10.8 12.2L20 3M16 7l3 3M14 9l2 2" />
                  </svg>
                </CardIcon>
                <Eyebrow style={{ marginBottom: '0.625rem' }}>Account recovery</Eyebrow>
                <PanelTitle>Recover your password</PanelTitle>
                <PanelText>Enter your email address. If an account exists, a reset link is sent to it.</PanelText>
                {error && (
                  <MessageBox $error role="alert">
                    {error}
                  </MessageBox>
                )}
                <Form onSubmit={handleSubmit}>
                  <Field>
                    <label htmlFor="fp-email">Email</label>
                    <Input
                      id="fp-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                  </Field>
                  <PrimaryButton type="submit" disabled={loading}>
                    {loading ? 'Sending reset link…' : 'Send reset link'}
                  </PrimaryButton>
                </Form>
              </>
            )}
          </CardBody>
          <CardFoot>
            Remembered it? <InlineLink to="/login">Back to login</InlineLink>
          </CardFoot>
        </AuthCard>
      </AuthLayout>
    </AuthPage>
  );
};

export default ForgotPassword;
